import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Flex,
} from '@chakra-ui/react';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GroupedOption,
  SelectFilterOption,
  ToggleFilterOption,
  GetFilterGroupedOptions,
} from '../../filterGroupedOptions';
import {
  groupStyles,
  groupBadgeStyles,
  articlesWidgetSelectStyles,
  SearchDropdownIndicator,
} from '../../styles';
import Select from 'react-select';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { TFunction } from 'i18next';
import { emptyPagedItems } from '@texas/utils/helpers/commonHelpers';
import { PaFilterOptions } from '@texas/api/endpoints/productApprovalsApi';
import { searchApi } from '@texas/api/endpoints/search/searchApi';
import {
  defaultEmptyFilters,
  ProductApprovalItem,
} from '@texas/api/endpoints/search/searchPaTypes';
import { PaSearchProps } from '@texas/components/widgets/ProductApprovals/types';
import { useSearchProductApprovals } from '@texas/components/shared/search/useSearchProductApprovals';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { ProductApprovalMenu } from '@texas/components/shared/productApproval/ProductApprovalMenu';
import { DataTableContainerFooter } from '@texas/components/shared/dataTable/DataTableContainerFooter';

interface ModalProps extends SharedDisclosureProps {
  filters: PaFilterOptions;
  searchProps: PaSearchProps;
  columns: (
    t: TFunction,
    containerRef: RefObject<HTMLDivElement>,
    onClose?: () => void,
  ) => ColumnDef<ProductApprovalItem, any>[];
}

const pageSize = 12;

export function PaOverviewModal({
  filters,
  searchProps,
  columns,
  isOpen,
  onClose,
}: ModalProps) {
  const { t } = useTranslation();
  return (
    <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="1400px">
        <ModalHeader>
          {t('widgets.widgetResultOverview')}
          <Text
            fontFamily="Instrument Sans"
            fontWeight="normal"
            fontSize="md"
            color="gray.100"
            _light={{ color: 'texas.bg.800' }}
          >
            {t('widgets.filterDisclaimer')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InnerComponent
            filters={filters}
            searchProps={searchProps}
            columns={columns}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

interface InnerProps {
  filters: PaFilterOptions;
  searchProps: PaSearchProps;
  columns: (
    t: TFunction,
    containerRef: RefObject<HTMLDivElement>,
    onClose?: () => void,
  ) => ColumnDef<ProductApprovalItem, any>[];
  onClose: () => void;
}

function InnerComponent({
  filters,
  searchProps,
  columns: widgetColumns,
  onClose,
}: InnerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    availableFilters,
    setActiveFiltersVariantResults,
    setActiveFilterTypes,
    setSearchPage,
    filterElements,
    searchPage,
    setSearchParamsWithRef,
    searchParamsRef,
    error,
    data: productApprovals,
    loading,
    refetchDebounce,
  } = useSearchProductApprovals({
    defaultFilters: filters,
    defaultSearchProps: searchProps,
    defaultPage: 1,
    limit: pageSize,
    request: searchApi.searchProductApprovals,
    defaultData: { pagedItems: emptyPagedItems },
    optOutDefaultOrder: true,
  });

  useEffect(() => {
    return productApprovalEvents.productApprovalChanged.sub(() => {
      refetchDebounce();
    });
  }, [refetchDebounce]);

  const columns = useMemo(() => {
    return widgetColumns(t, containerRef, onClose);
  }, [widgetColumns, t, onClose]);

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const rowIds = useMemo(
    () => Object.keys(selectedRows).map((x) => parseInt(x)),
    [selectedRows],
  );

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );
  return (
    <>
      <Flex flexDir="column" ref={containerRef}>
        <Flex alignItems="center" gap={2}>
          <Box flexShrink={0}>
            <Select<
              SelectFilterOption | ToggleFilterOption,
              false,
              GroupedOption
            >
              inputId="clickableInput"
              placeholder=""
              styles={articlesWidgetSelectStyles}
              components={{ DropdownIndicator: SearchDropdownIndicator }}
              options={GetFilterGroupedOptions(availableFilters)}
              formatGroupLabel={formatGroupLabel}
              onChange={(value) => {
                if (!value) return;
                const type = value.value;
                setActiveFiltersVariantResults((s) => [
                  ...s,
                  {
                    optionType: type,
                    ...defaultEmptyFilters,
                    isPopulated: false,
                  },
                ]);
                setActiveFilterTypes((s) => [...s, [true, type]]);
                setSearchPage((s) => ({
                  ...s,
                  filters: [...s.filters, type],
                }));
              }}
              value={null}
            />
          </Box>
          <ProductApprovalMenu
            selectedIds={rowIds}
            allIds={productApprovals?.allPaIdsResult ?? []}
          />
        </Flex>

        <Box
          width="100%"
          display="grid"
          gridTemplateColumns="repeat(6, 1fr)"
          gridGap={1}
          rowGap={1}
        >
          <Box height="fit-content" mt="auto" gridColumn="span 2">
            <SearchFilterInput
              placeholder={t('productApproval.search')}
              value={searchPage.searchParams.searchTerm}
              onChange={(searchTerm) =>
                setSearchParamsWithRef({
                  ...searchParamsRef.current,
                  searchTerm,
                  page: 1,
                })
              }
            />
          </Box>
          {filterElements}
        </Box>
      </Flex>
      <DataTableContainer
        p={4}
        w="100%"
        minH={0}
        display="flex"
        flexDir="column"
        error={error}
        css={{ ' th, td': { whiteSpace: 'normal' } }}
        tableId="pa-overview-modal"
        datatable={{
          selectionProps: {
            setSelectedRows: setSelectedRows,
            selectedRows: selectedRows,
          },
          data: productApprovals?.pagedItems.items ?? [],
          columns: columns,
          isLoading: loading,
          rowProps: {
            getRowId: (originalRow, _) => {
              return originalRow.id.toString();
            },
            uniqueRowStyle: (originalRow) => {
              if (originalRow.mainSupplier) {
                return {
                  fontStyle: 'italic',
                };
              }
              return null;
            },
          },
          sorted: {
            onSortedChange: ({ key, desc }) => {
              setSearchParamsWithRef({
                ...searchParamsRef.current,
                sortBy: key,
                sortDesc: desc,
                page: 1,
              });
            },
            key: searchPage.searchParams['sortBy'],
            desc: searchPage.searchParams['sortDesc'],
          },
        }}
        pagination={{
          totalItems: productApprovals?.pagedItems.totalItems ?? 0,
          pageSize: pageSize,
          currentPage: searchPage.searchParams.page,
          onPageChange: (page) =>
            setSearchParamsWithRef({
              ...searchParamsRef.current,
              page,
            }),
        }}
        footer={
          <DataTableContainerFooter
            selectedRows={Object.keys(selectedRows).length}
            onClear={() => setSelectedRows({})}
            text={t('productApproval.productApprovalSelected')}
          />
        }
      />
    </>
  );
}
